import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, 
  Link2, 
  Check, 
  X,
  ExternalLink,
  AlertCircle
} from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Platform icons (simplified - using first letter)
const getPlatformIcon = (platform) => {
  const icons = {
    facebook: "f",
    tiktok: "T",
    youtube: "Y",
    instagram: "I",
    snapchat: "S",
    pinterest: "P",
    x: "X",
    threads: "t",
    douyin: "D",
    etsy: "E",
    zazzle: "Z",
    shopify: "S",
    canva: "C",
    printfy: "P",
    redbubble: "R",
    spreadshirt: "S",
    printiful: "P"
  };
  return icons[platform] || platform.charAt(0).toUpperCase();
};

const getPlatformColor = (platform) => {
  const colors = {
    facebook: "#1877F2",
    tiktok: "#000000",
    youtube: "#FF0000",
    instagram: "#E1306C",
    snapchat: "#FFFC00",
    pinterest: "#E60023",
    x: "#000000",
    threads: "#000000",
    douyin: "#000000",
    etsy: "#F16521",
    zazzle: "#000000",
    shopify: "#96BF48",
    canva: "#00C4CC",
    printfy: "#29abe2",
    redbubble: "#E41321",
    spreadshirt: "#00B2A5",
    printiful: "#3e4c59"
  };
  return colors[platform] || "#000000";
};

const SettingsPage = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/social-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platform) => {
    setConnecting(platform);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/social-accounts/connect`,
        { platform },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Connected to ${platform}!`);
      fetchAccounts();
    } catch (error) {
      const message = error.response?.data?.detail || "Connection failed";
      toast.error(message);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/social-accounts/${platform}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Disconnected from ${platform}`);
      fetchAccounts();
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const socialAccounts = accounts.filter((a) =>
    ["facebook", "tiktok", "youtube", "instagram", "snapchat", "pinterest", "x", "threads", "douyin"].includes(a.platform)
  );

  const marketplaceAccounts = accounts.filter((a) =>
    ["etsy", "zazzle", "shopify", "canva", "printfy", "redbubble", "spreadshirt", "printiful"].includes(a.platform)
  );

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-2 mb-2">
          <SettingsIcon className="w-6 h-6 text-[#7C3AED]" />
          <span className="text-sm font-mono uppercase tracking-widest text-gray-500">
            SETTINGS
          </span>
        </div>
        <h1 className="text-4xl font-extrabold tracking-tighter mb-4">
          Connected Accounts
        </h1>
        <p className="text-gray-600">
          Connect your social media accounts and marketplaces to post content everywhere.
        </p>
      </motion.div>

      {/* Placeholder Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8 p-4 bg-[#CCFF00]/20 border-2 border-[#CCFF00] flex gap-3"
      >
        <AlertCircle className="w-5 h-5 text-[#7C3AED] flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-sm">Integration Placeholders</div>
          <p className="text-sm text-gray-600">
            Platform connections are currently placeholders. In production, these would redirect to 
            OAuth flows for each platform. You can "connect" to simulate the feature.
          </p>
        </div>
      </motion.div>

      {/* Social Media Platforms */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
          SOCIAL MEDIA PLATFORMS
        </h2>
        <div className="platform-grid">
          {loading
            ? [...Array(9)].map((_, i) => (
                <div key={i} className="h-24 border-2 border-gray-200 skeleton" />
              ))
            : socialAccounts.map((account) => (
                <div
                  key={account.platform}
                  className={`neo-card p-4 ${account.connected ? "bg-green-50" : ""}`}
                  data-testid={`platform-${account.platform}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center font-bold text-white"
                      style={{ backgroundColor: getPlatformColor(account.platform) }}
                    >
                      {getPlatformIcon(account.platform)}
                    </div>
                    {account.connected ? (
                      <div className="w-6 h-6 bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 bg-gray-200 flex items-center justify-center">
                        <X className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="font-bold text-sm capitalize mb-1">
                    {account.platform}
                  </div>
                  {account.connected ? (
                    <>
                      <div className="text-xs text-gray-500 mb-2 font-mono truncate">
                        {account.username}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDisconnect(account.platform)}
                        className="w-full border border-red-500 text-red-500 hover:bg-red-50 text-xs"
                        data-testid={`disconnect-${account.platform}`}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleConnect(account.platform)}
                      disabled={connecting === account.platform}
                      className="w-full bg-black text-white hover:bg-[#CCFF00] hover:text-black text-xs"
                      data-testid={`connect-${account.platform}`}
                    >
                      {connecting === account.platform ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
                      ) : (
                        <>
                          <Link2 className="w-3 h-3 mr-1" />
                          Connect
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
        </div>
      </motion.div>

      {/* Marketplace Platforms */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
          MARKETPLACES
        </h2>
        <div className="platform-grid">
          {loading
            ? [...Array(8)].map((_, i) => (
                <div key={i} className="h-24 border-2 border-gray-200 skeleton" />
              ))
            : marketplaceAccounts.map((account) => (
                <div
                  key={account.platform}
                  className={`neo-card p-4 ${account.connected ? "bg-green-50" : ""}`}
                  data-testid={`platform-${account.platform}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center font-bold text-white"
                      style={{ backgroundColor: getPlatformColor(account.platform) }}
                    >
                      {getPlatformIcon(account.platform)}
                    </div>
                    {account.connected ? (
                      <div className="w-6 h-6 bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 bg-gray-200 flex items-center justify-center">
                        <X className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="font-bold text-sm capitalize mb-1">
                    {account.platform}
                  </div>
                  {account.connected ? (
                    <>
                      <div className="text-xs text-gray-500 mb-2 font-mono truncate">
                        {account.username}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDisconnect(account.platform)}
                        className="w-full border border-red-500 text-red-500 hover:bg-red-50 text-xs"
                        data-testid={`disconnect-${account.platform}`}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleConnect(account.platform)}
                      disabled={connecting === account.platform}
                      className="w-full bg-black text-white hover:bg-[#CCFF00] hover:text-black text-xs"
                      data-testid={`connect-${account.platform}`}
                    >
                      {connecting === account.platform ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
                      ) : (
                        <>
                          <Link2 className="w-3 h-3 mr-1" />
                          Connect
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
        </div>
      </motion.div>

      {/* Cross-Platform Posting Info */}
      {user?.is_premium && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-6 neo-card bg-[#CCFF00]/10"
        >
          <h3 className="font-bold text-lg mb-2">
            Cross-Platform Posting (Premium Feature)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            As a premium member, you can post your content to all connected platforms 
            with a single click. Connect your accounts above to get started.
          </p>
          <div className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            <span className="text-sm font-mono">
              {accounts.filter((a) => a.connected).length} / {accounts.length} platforms connected
            </span>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SettingsPage;
